import {
  Component,
  Input,
  Output,
  ChangeDetectorRef,
  ViewChild,
  EventEmitter,
  AfterViewInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ProductHistory } from '@models/product';
import { VendorOrderService } from '@services/vendor-order.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { ImageDialogComponent } from '@components/admin/image/image-dialog/image-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ImageService } from '@services/image.service';
import { OrderEditDialogComponent } from '@components/admin/patient/order-edit-dialog/order-edit-dialog.component';

@Component({
  selector: 'app-inventory-history',
  templateUrl: './inventory-history.component.html',
  styleUrls: ['./inventory-history.component.css'],
})
export class InventoryHistoryComponent implements AfterViewInit {
  @Input() public product: FormGroup = null;
  @Input() public showParts: boolean = false;
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @Output() clickField = new EventEmitter<boolean>();
  dataSource = new MatTableDataSource<ProductHistory>();
  displayedColumns: string[] = [
    'invoiceNumber',
    'date',
    'shipping',
    'tax',
    'cost',
    'duty',
    'patientInvoiceNumber',
    'patientStock',
    'status',
    'view',
    'log',
  ];
  defaultSortColumn: string = 'date';
  defaultSortDirection: 'asc' | 'desc' = 'desc';
  loading: boolean = true;
  imageOpen: string | null;
  imageDialogRef: MatDialogRef<ImageDialogComponent>;

  constructor(
    private vendorOrderService: VendorOrderService,
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private dialog: MatDialog,
    private imageService: ImageService
  ) {}

  ngAfterViewInit() {
    if (this.product.value._id) {
      this.getProductHistory();
    } else {
      this.loading = false;
      this.cdRef.detectChanges();
    }
  }

  getProductHistory() {
    const historyRequest = this.showParts
      ? this.vendorOrderService.getPartHistory(this.product.value._id)
      : this.vendorOrderService.getProductHistory(this.product.value._id);

    historyRequest.subscribe((productHistory: ProductHistory[]) => {
      this.dataSource.data = this.sortHistoryByDate(
        productHistory,
        this.defaultSortDirection
      );
      this.loading = false;
      this.cdRef.detectChanges();
      setTimeout(() => this.initSort());
    });
  }

  private getHistoryDate(item: ProductHistory): number {
    const raw = item.date ?? item.order?.date;
    return raw ? new Date(raw).getTime() : 0;
  }

  private sortHistoryByDate(
    history: ProductHistory[],
    direction: 'asc' | 'desc'
  ): ProductHistory[] {
    const multiplier = direction === 'desc' ? -1 : 1;
    return history.slice().sort(
      (a, b) => multiplier * (this.getHistoryDate(a) - this.getHistoryDate(b))
    );
  }

  onClickField() {
    this.clickField.emit(true);
  }

  initSort() {
    if (!this.sort) {
      return;
    }

    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'date') {
        return this.getHistoryDate(item);
      }
      switch (property) {
        case 'invoiceNumber':
          return item.invoiceNumber?.toUpperCase() ?? '';
        case 'cost':
          return item.order?.products?.[0]?.cost ?? 0;
        case 'tax':
          return item.product?.tax ?? item.order?.products?.[0]?.tax ?? 0;
        case 'shipping':
          return item.product?.shipping ?? item.order?.products?.[0]?.shipping ?? 0;
        case 'duty':
          return item.order?.products?.[0]?.duty ?? false;
        case 'notes':
          return item.order?.notes?.toUpperCase() ?? '';
        case 'patientInvoiceNumber':
          return item.order?.invoiceNumber?.toUpperCase() ?? '';
        case 'patientStock':
          return item.patient
            ? item.patient.lastName.toUpperCase() +
                ',' +
                item.patient.firstName.toUpperCase()
            : 'STOCK';
        case 'status':
          return item.status?.toUpperCase() ?? '';
        default:
          const value = item[property];
          return typeof value === 'string' ? value.toUpperCase() : value;
      }
    };

    this.dataSource.sort = this.sort;
    this.sort.active = this.defaultSortColumn;
    this.sort.direction = this.defaultSortDirection;
    this.cdRef.detectChanges();
  }

  openPatient(patientId: string | number) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/admin/patient', patientId])
    );
    window.open(url, '_blank');
  }

  openOrderEdit($event: Event, history: ProductHistory): void {
    $event.preventDefault();
    $event.stopPropagation();
    const patientId = history?.patient?._id || null;
    const orderId = history?.order?._id;
    if (!patientId || !orderId) {
      return;
    }

    this.dialog.open(OrderEditDialogComponent, {
      width: '96vw',
      maxWidth: '96vw',
      height: '92vh',
      maxHeight: '92vh',
      autoFocus: false,
      panelClass: 'order-edit-dialog-panel',
      data: { patientId, orderId },
    });
  }

  openImage(imageId?: string) {
    this.imageService.getImageUrl(imageId).subscribe((res) => {
      this.imageDialogRef = this.dialog.open(ImageDialogComponent, {
        width: '',
        height: '',
        data: { image: res, imageId: imageId, message: '' },
      });
    });
  }

  onClose() {
    this.imageOpen = null;
  }
}
